/*
 * PDP gallery / zoom-modal product video (snippets/product-gallery-video.liquid).
 * - Loads the file only when this copy is on screen. A copy hidden with display:none (the other device's copy,
 *   or a slide out of view) never intersects, so it never downloads.
 * - Plays while any pixel is visible and pauses when swiped or scrolled away, which the gallery never did
 *   because media-gallery.js exits early without a thumbnail strip.
 * - Inside the zoom modal it waits for the modal to open, because the closed modal is visibility:hidden and
 *   would otherwise count as on screen.
 */
class ProductGalleryVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.source = this.video?.querySelector('source[data-src]');
    if (!this.source?.dataset.src || !('IntersectionObserver' in window)) return;

    this.isVisible = false;
    this.onPlay = () => {
      if (!this.isVisible) this.video.pause();
    };
    this.video.addEventListener('play', this.onPlay);

    this.observer = new IntersectionObserver((entries) => {
      this.isVisible = entries.some((entry) => entry.isIntersecting);
      if (this.isVisible) {
        this.start();
      } else if (!this.video.paused) {
        this.video.pause();
      }
    }, { threshold: 0 });

    this.modal = this.closest('product-modal');
    if (!this.modal) {
      this.observer.observe(this);
      return;
    }

    this.modalObserver = new MutationObserver(() => this.syncWithModal());
    this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
    this.syncWithModal();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.modalObserver?.disconnect();
    this.video?.removeEventListener('play', this.onPlay);
  }

  syncWithModal() {
    if (this.modal.hasAttribute('open')) {
      this.observer.observe(this);
      return;
    }
    this.observer.unobserve(this);
    this.isVisible = false;
    if (!this.video.paused) this.video.pause();
  }

  start() {
    if (!this.source.getAttribute('src')) {
      this.source.src = this.source.dataset.src;
      this.video.load();
    }
    if (this.video.paused) this.video.play()?.catch(() => {});
  }
}

if (!customElements.get('product-gallery-video')) {
  customElements.define('product-gallery-video', ProductGalleryVideo);
}
